import * as Linking from "expo-linking";
import * as Notifications from "expo-notifications";

export type SystemNotificationPermission = {
    allowed: boolean;
    /** False after the user has denied the OS prompt; iOS will not show it again. */
    canAskAgain: boolean;
};

export type SystemNotificationPermissionRequestResult =
    | "granted"
    | "denied"
    | "blocked";

function areSystemNotificationsAllowed(
    settings: Notifications.NotificationPermissionsStatus,
): boolean {
    return (
        settings.granted ||
        settings.ios?.status ===
            Notifications.IosAuthorizationStatus.PROVISIONAL ||
        settings.ios?.status === Notifications.IosAuthorizationStatus.EPHEMERAL
    );
}

function fromSettings(
    settings: Notifications.NotificationPermissionsStatus,
): SystemNotificationPermission {
    return {
        allowed: areSystemNotificationsAllowed(settings),
        canAskAgain: settings.canAskAgain,
    };
}

/** Reads whether the OS currently allows notifications for this app. */
export async function getSystemNotificationPermission(): Promise<SystemNotificationPermission> {
    const settings = await Notifications.getPermissionsAsync();
    return fromSettings(settings);
}

/**
 * Requests OS notification permission when the system dialog can still be shown.
 * After a denial, iOS never shows that dialog again — returns `"blocked"`.
 */
export async function requestSystemNotificationPermission(): Promise<SystemNotificationPermissionRequestResult> {
    const existing = await Notifications.getPermissionsAsync();
    if (areSystemNotificationsAllowed(existing)) {
        return "granted";
    }

    if (!existing.canAskAgain) {
        return "blocked";
    }

    const requested = await Notifications.requestPermissionsAsync({
        ios: {
            allowAlert: true,
            allowBadge: true,
            allowSound: true,
        },
    });
    return areSystemNotificationsAllowed(requested) ? "granted" : "denied";
}

/** Opens the OS Settings screen for this app (the only way to re-enable after a denial). */
export async function openSystemNotificationSettings(): Promise<void> {
    await Linking.openSettings();
}
